/**
 * Team Service
 * ============
 * Handles organization and team member management.
 */

import { supabase } from '../lib/supabase'
import { emailService } from './emailService'
import type { UserRole, SocialLinks, Organization as DbOrganization } from '../types/database.types'

/**
 * Check if the current session is valid before making authenticated queries.
 * Returns true if session exists and is not expired.
 */
async function isSessionValid(): Promise<boolean> {
  try {
    const { data: { session }, error } = await supabase.auth.getSession()
    if (error || !session) return false

    // Check if token is about to expire (within 60 seconds)
    const expiresAt = session.expires_at
    if (expiresAt && expiresAt * 1000 < Date.now() + 60000) {
      // Try to refresh the session
      const { error: refreshError } = await supabase.auth.refreshSession()
      return !refreshError
    }

    return true
  } catch {
    return false
  }
}

// Re-export Organization type from database types
export type Organization = DbOrganization

export interface OrganizationMember {
  id: string
  organization_id: string
  user_id: string
  role: UserRole
  is_active: boolean | null
  joined_at: string
}

export interface TeamMember extends OrganizationMember {
  user: {
    id: string
    email: string
    full_name: string | null
    avatar_url: string | null
  }
}

export interface TeamInvite {
  email: string
  role: UserRole
  organizationId: string
}

export interface CreateOrganizationData {
  name: string
  slug: string
  description?: string
  owner_id: string
}

export const teamService = {
  /**
   * Get or create an organization for a user
   */
  async getOrCreateOrganization(userId: string, userName: string): Promise<Organization | null> {
    // Validate session before making queries - prevents 406 errors from stale tokens
    if (!(await isSessionValid())) {
      console.warn('[teamService] Session invalid or expired, skipping organization query')
      return null
    }

    try {
      // First, check if user owns an organization
      const { data: existingOrg, error: ownerError } = await supabase
        .from('organizations')
        .select('*')
        .eq('owner_id', userId)
        .single()

      // Handle 406 errors specifically - these indicate session/auth issues
      if (ownerError && 'code' in ownerError && ownerError.code === '406') {
        console.warn('[teamService] 406 error on organizations query - session may be stale')
        return null
      }

      if (existingOrg) {
        return existingOrg
      }

      // Check if user is a member of any organization
      const { data: membership, error: memberError } = await supabase
        .from('organization_members')
        .select('organization_id')
        .eq('user_id', userId)
        .eq('is_active', true)
        .single()

      // Handle 406 errors
      if (memberError && 'code' in memberError && memberError.code === '406') {
        console.warn('[teamService] 406 error on organization_members query - session may be stale')
        return null
      }

      if (membership) {
        const { data: org } = await supabase
          .from('organizations')
          .select('*')
          .eq('id', membership.organization_id)
          .single()
        return org
      }

      // Create a new organization for this user
      const slug = generateSlug(userName || 'organization')
      const { data: newOrg, error: createError } = await supabase
        .from('organizations')
        .insert({
          name: `${userName}'s Team`,
          slug,
          owner_id: userId,
          subscription_tier: 'corporate', // Will inherit from user's tier
          max_seats: 10,
        })
        .select()
        .single()

      if (createError) {
        console.error('Failed to create organization:', createError)
        return null
      }

      // Add owner as a member with org_admin role
      if (newOrg) {
        await supabase.from('organization_members').insert({
          organization_id: newOrg.id,
          user_id: userId,
          role: 'org_admin',
          is_active: true,
        })
      }

      return newOrg
    } catch (error) {
      console.error('[teamService] Error in getOrCreateOrganization:', error)
      return null
    }
  },

  /**
   * Get organization by ID
   */
  async getOrganization(organizationId: string): Promise<Organization | null> {
    if (!(await isSessionValid())) {
      console.warn('[teamService] Session invalid, skipping getOrganization')
      return null
    }

    try {
      const { data, error } = await supabase
        .from('organizations')
        .select('*')
        .eq('id', organizationId)
        .single()

      if (error) {
        console.error('Failed to get organization:', error)
        return null
      }

      return data
    } catch (error) {
      console.error('[teamService] Error in getOrganization:', error)
      return null
    }
  },

  /**
   * Get all team members for an organization
   */
  async getTeamMembers(organizationId: string): Promise<TeamMember[]> {
    if (!(await isSessionValid())) {
      console.warn('[teamService] Session invalid, skipping getTeamMembers')
      return []
    }

    try {
      const { data, error } = await supabase
        .from('organization_members')
        .select(`
          *,
          user:users!organization_members_user_id_fkey(id, email, full_name, avatar_url)
        `)
        .eq('organization_id', organizationId)
        .eq('is_active', true)
        .order('joined_at', { ascending: true })

      if (error) {
        console.error('Failed to get team members:', error)
        return []
      }

      return (data || []) as unknown as TeamMember[]
    } catch (error) {
      console.error('[teamService] Error in getTeamMembers:', error)
      return []
    }
  },

  /**
   * Invite a new team member by email
   * Note: In a real app, this would send an email invitation
   * For now, it creates the user if they exist, or queues an invite
   */
  async inviteTeamMember(invite: TeamInvite): Promise<{ success: boolean; message: string }> {
    // Check if email already exists in the system
    const { data: existingUser } = await supabase
      .from('users')
      .select('id, email')
      .eq('email', invite.email)
      .single()

    if (existingUser) {
      // Check if already a member
      const { data: existingMember } = await supabase
        .from('organization_members')
        .select('id')
        .eq('organization_id', invite.organizationId)
        .eq('user_id', existingUser.id)
        .single()

      if (existingMember) {
        return { success: false, message: 'This user is already a team member.' }
      }

      // Add them directly as a member
      const { error } = await supabase.from('organization_members').insert({
        organization_id: invite.organizationId,
        user_id: existingUser.id,
        role: invite.role,
        is_active: true,
      })

      if (error) {
        console.error('Failed to add team member:', error)
        return { success: false, message: 'Failed to add team member.' }
      }

      return { success: true, message: `${invite.email} has been added to your team.` }
    }

    // User doesn't exist yet - send an email invite
    // Get organization details for the email
    const org = await this.getOrganization(invite.organizationId)
    if (org) {
      await emailService.sendTeamInvitation(invite.email, {
        inviterName: 'Your team admin',
        organizationName: org.name,
        role: invite.role === 'org_admin' ? 'Admin' : 'Provider',
        inviteLink: `${window.location.origin}/signup?org=${invite.organizationId}`,
      })
    }

    return {
      success: true,
      message: `Invitation sent to ${invite.email}. They'll be added when they sign up.`,
    }
  },

  /**
   * Update a team member's role
   */
  async updateMemberRole(
    memberId: string,
    newRole: UserRole
  ): Promise<boolean> {
    const { error } = await supabase
      .from('organization_members')
      .update({ role: newRole })
      .eq('id', memberId)

    if (error) {
      console.error('Failed to update member role:', error)
      return false
    }

    return true
  },

  /**
   * Remove a team member (deactivate)
   */
  async removeMember(memberId: string): Promise<boolean> {
    const { error } = await supabase
      .from('organization_members')
      .update({ is_active: false })
      .eq('id', memberId)

    if (error) {
      console.error('Failed to remove team member:', error)
      return false
    }

    return true
  },

  /**
   * Update organization details
   */
  async updateOrganization(
    organizationId: string,
    updates: {
      name?: string
      description?: string | null
      logo_url?: string | null
      website_url?: string | null
      social_links?: SocialLinks | null
    }
  ): Promise<Organization | null> {
    // Cast social_links to match database Json type
    const dbUpdates = {
      ...updates,
      social_links: updates.social_links as unknown as Record<string, string | undefined>,
      updated_at: new Date().toISOString()
    }
    const { data, error } = await supabase
      .from('organizations')
      .update(dbUpdates)
      .eq('id', organizationId)
      .select()
      .single()

    if (error) {
      console.error('Failed to update organization:', error)
      return null
    }

    return data
  },

  /**
   * Get seat usage for an organization
   */
  async getSeatUsage(organizationId: string): Promise<{ current: number; max: number }> {
    if (!(await isSessionValid())) {
      console.warn('[teamService] Session invalid, skipping getSeatUsage')
      return { current: 0, max: 1 }
    }

    try {
      const { count } = await supabase
        .from('organization_members')
        .select('*', { count: 'exact', head: true })
        .eq('organization_id', organizationId)
        .eq('is_active', true)

      const { data: org } = await supabase
        .from('organizations')
        .select('max_seats')
        .eq('id', organizationId)
        .single()

      return {
        current: count || 0,
        max: org?.max_seats || 1,
      }
    } catch (error) {
      console.error('[teamService] Error in getSeatUsage:', error)
      return { current: 0, max: 1 }
    }
  },
}

/**
 * Generate a URL-safe slug from a name
 */
function generateSlug(name: string): string {
  const base = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 50)

  // Add random suffix for uniqueness
  const suffix = Math.random().toString(36).substring(2, 8)
  return `${base}-${suffix}`
}
